import { html, css, LitElement, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { hermesClient } from "../hermes-singleton.js";
import {
  executionsStore,
  normalizeExecutionFromApi,
  unwrapPfmInheritanceMetadataFromRunResponse,
} from "../executions-store.ts";
import { templateAllowsNodeDeepLearning } from "../pfm-run-eligibility.ts";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import { formatMs } from "../format.ts";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { ProjectExecute, ProjectTemplate, RunLearningMode } from "../../types/ead-types.ts";
import {
  loadCachedTemplateSummaries,
  saveCachedTemplateSummaries,
  templateFromListSummary,
  templatePfmNodeCount,
} from "../template-list-cache.ts";
import { getJobTypePromptParts } from "./explore-types.ts";
import {
  formatLoginInfoPrompt,
  loginInfoPreviewMarkdown,
  parseLoginInfoPrompt,
} from "../login-info-prompt.ts";
import { notifyOperator } from "../operator-notify.ts";
import { t, type UiLocale } from "../i18n.ts";
import {
  answerLanguageLabel,
  defaultAnswerLanguage,
  type AiAnswerLanguage,
} from "../ai-answer-language.ts";
import "../components/ai-answer-language-field.ts";
import { clearBodyPortal, renderBodyPortal } from "../body-portal.ts";

/** Run confirm modal state: template fields plus execution learning metadata. */
export type RunConfirmForm = Partial<ProjectTemplate> &
  Pick<Partial<ProjectExecute>, "runPurpose" | "evidenceSource" | "learningExclusionReason"> & {
    aiAnswerLanguage?: AiAnswerLanguage;
    learningMode?: RunLearningMode;
  };

const DEFAULT_AI_PROMPT = `Goal: Build and continuously refine a PFM mindmap for this target.

Working style:
1) Explore broad first, then deepen into key journeys.
2) Report findings incrementally with clear milestones.
3) Keep a running PFM map and improve it as evidence increases.
4) Include edge cases, failures, and alternate paths.
5) When studying the app, focus on product functions for the PFM. Sample data may showcase the UI but must not mislead: the mindmap reflects product functions, not that sample data.
6) Whenever a PFM node or feature is newly identified or re-confirmed, capture a screenshot, send it back with your report (e.g. report_running_step), and ensure it is recorded with that finding.

Deliverables:
- Real-time PFM finding updates while running
- Multi-level PFM mindmap (hierarchy with depth, not flat) so end users can scan product features easily
- Concise run summary and notable risks`;

type PromptPhaseKey = "aiPromptPart1" | "aiPromptPart2";

const PHASE_PROMPT_META: Array<{ key: PromptPhaseKey; label: string; title: string }> = [
  { key: "aiPromptPart1", label: "Login Info", title: "Login Info" },
  { key: "aiPromptPart2", label: "Explore", title: "Explore" },
];

@customElement("ead-projects-view")
export class EadProjectsView extends LitElement {
  @property({ type: String }) locale: UiLocale = "en";

  private _t(key: string, params?: Record<string, string | number>): string {
    return t(key, params, this.locale);
  }

  static styles = css`
    :host { display: block; height: 100%; }
    .projects-container { display: flex; height: 100%; }
    .template-list { width: 320px; min-width: 320px; border-right: 1px solid var(--border, #2e2f33); overflow-y: auto; }
    .template-list-header { padding: 16px 20px; border-bottom: 1px solid var(--border, #2e2f33); display: flex; justify-content: space-between; align-items: center; }
    .template-list-header h2 { margin: 0; font-size: 16px; font-weight: 600; }
    .btn { padding: 6px 14px; border-radius: 6px; border: 1px solid var(--border, #2e2f33); background: transparent; color: var(--fg, #e4e5e7); font-size: 12px; cursor: pointer; }
    .btn:hover { background: rgba(255,255,255,0.05); }
    .btn-primary { background: var(--accent, #6ea8fe); color: #fff; border-color: var(--accent, #6ea8fe); }
    .btn-run { background: #4caf50; color: #fff; border-color: #4caf50; font-weight: 600; padding: 8px 20px; font-size: 13px; }
    .btn-run:hover { background: #43a047; }
    .btn-run:disabled { opacity: 0.5; cursor: not-allowed; }
    .template-item { padding: 12px 20px; border-bottom: 1px solid var(--border, #2e2f33); cursor: pointer; transition: background 0.1s; }
    .template-item:hover { background: rgba(255,255,255,0.03); }
    .template-item.selected { background: rgba(255,255,255,0.06); border-left: 3px solid var(--accent, #6ea8fe); }
    .template-item .name { font-size: 13px; font-weight: 500; color: var(--fg, #e4e5e7); margin-bottom: 4px; }
    .template-item .url { font-size: 11px; color: var(--muted, #8b8d93); }
    .template-item .meta { font-size: 11px; color: var(--muted, #8b8d93); margin-top: 4px; }
    .detail-panel { flex: 1; overflow-y: auto; padding: 24px 28px; }
    .detail-panel h3 { margin: 0 0 16px; font-size: 18px; font-weight: 600; }
    .field-group { margin-bottom: 16px; }
    .field-group label { display: block; font-size: 12px; font-weight: 500; color: var(--muted, #8b8d93); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px; }
    .field-group .value { font-size: 14px; color: var(--fg, #e4e5e7); }
    .field-group textarea, .field-group input, .field-group select { width: 100%; box-sizing: border-box; padding: 8px 12px; border-radius: 6px; border: 1px solid var(--border, #2e2f33); background: var(--bg-surface, #222326); color: var(--fg, #e4e5e7); font-size: 13px; font-family: inherit; resize: vertical; }
    .field-group textarea { min-height: 100px; font-family: 'SF Mono', Monaco, monospace; }
    .actions { display: flex; gap: 8px; margin-top: 20px; }
    .loading { display: flex; align-items: center; justify-content: center; height: 200px; color: var(--muted, #8b8d93); font-size: 14px; }
    .empty { display: flex; align-items: center; justify-content: center; height: 100%; color: var(--muted, #8b8d93); font-size: 14px; flex-direction: column; gap: 12px; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; background: rgba(110,168,254,0.15); color: var(--accent, #6ea8fe); }
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.55);
      display: grid;
      place-items: center;
      z-index: 1100;
      padding: 20px;
      font-family: inherit;
      color: var(--fg, #e4e5e7);
    }
    .modal-backdrop--learning {
      background: rgba(6, 8, 14, 0.78);
      backdrop-filter: blur(4px);
    }
    .learning-mode-cards {
      display: grid;
      gap: 12px;
      margin: 4px 0 8px;
    }
    .learning-mode-card {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      text-align: left;
      width: 100%;
      padding: 14px 16px;
      border-radius: 10px;
      border: 1px solid #4a5368;
      background: rgba(255, 255, 255, 0.03);
      cursor: pointer;
      color: inherit;
      font: inherit;
    }
    .learning-mode-card:hover:not(:disabled):not(.selected) {
      border-color: #5c6478;
      background: rgba(255, 255, 255, 0.05);
    }
    .learning-mode-card.selected {
      border-color: #8b96ad;
      background: rgba(255, 255, 255, 0.07);
    }
    .learning-mode-card:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .learning-mode-card__radio {
      flex-shrink: 0;
      width: 16px;
      height: 16px;
      margin-top: 3px;
      border-radius: 50%;
      border: 2px solid #6b758c;
      background: transparent;
      box-sizing: border-box;
    }
    .learning-mode-card.selected .learning-mode-card__radio {
      border-color: #a8b4cc;
      background: #a8b4cc;
    }
    .learning-mode-card__body {
      min-width: 0;
      flex: 1;
    }
    .learning-mode-card__title {
      font-weight: 600;
      font-size: 14px;
      margin-bottom: 6px;
      color: var(--fg, #e4e5e7);
    }
    .learning-mode-card.selected .learning-mode-card__title {
      font-weight: 600;
    }
    .learning-mode-card__desc {
      font-size: 12px;
      color: var(--muted, #8b8d93);
      line-height: 1.45;
    }
    .modal {
      width: min(860px, calc(100vw - 36px));
      max-height: calc(100vh - 40px);
      overflow: hidden;
      background: var(--bg, #161920);
      border: 1px solid var(--border, #2e2f33);
      border-radius: 12px;
      padding: 0;
      box-shadow: 0 22px 48px rgba(0, 0, 0, 0.48);
    }
    .modal--compact {
      width: min(560px, calc(100vw - 36px));
    }
    .modal--learning {
      width: min(520px, calc(100vw - 36px));
      background: #2a3142;
      border: 1px solid #5c6478;
      box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45);
    }
    .modal--learning .modal-header {
      background: #323b4f;
      border-bottom: 1px solid #4a5368;
    }
    .modal--learning .modal-body {
      background: #2a3142;
    }
    .modal--learning .btn-primary {
      background: #4a5d78;
      border-color: #5a6d88;
      color: #f4f5f7;
    }
    .modal--learning .btn-primary:hover:not(:disabled) {
      background: #556889;
      border-color: #647394;
    }
    .modal--learning .learning-mode-card:not(.selected) {
      background: rgba(255, 255, 255, 0.04);
      border-color: #4a5368;
    }
    .modal--learning .learning-mode-card.selected {
      border-color: #8b96ad;
      background: rgba(255, 255, 255, 0.08);
    }
    .learning-mode-summary {
      margin: 0 0 14px;
      font-size: 12px;
      color: var(--muted, #8b8d93);
    }
    .learning-mode-summary button {
      margin-left: 8px;
      padding: 0;
      border: none;
      background: none;
      color: var(--accent, #6ea8fe);
      cursor: pointer;
      font-size: 12px;
    }
    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 18px;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 75%, transparent);
      background: color-mix(in srgb, var(--bg-elevated, #1b1f2a) 90%, transparent);
    }
    .modal-title-wrap { min-width: 0; }
    .modal h3 { margin: 0; font-size: 17px; line-height: 1.2; }
    .modal-subtitle { margin-top: 4px; font-size: 12px; color: var(--muted, #8b8d93); }
    .modal-header-actions { display: inline-flex; gap: 8px; flex-shrink: 0; }
    .modal-body { padding: 16px 18px 18px; overflow: auto; max-height: calc(100vh - 140px); }
    .modal .row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
    .prompt-editor-tabs {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 10px;
      flex-wrap: wrap;
    }
    .prompt-editor-phase-tabs,
    .prompt-editor-mode-tabs {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
    .prompt-editor-tab {
      border: 1px solid var(--border, #2e2f33);
      background: transparent;
      color: var(--muted, #8b8d93);
      border-radius: 8px;
      padding: 6px 10px;
      font-size: 12px;
      cursor: pointer;
    }
    .prompt-editor-tab.active {
      border-color: color-mix(in srgb, var(--accent, #6ea8fe) 65%, transparent);
      color: var(--fg, #e4e5e7);
      background: color-mix(in srgb, var(--accent, #6ea8fe) 18%, transparent);
    }
    .prompt-editor-preview {
      min-height: 180px;
      border-radius: 8px;
      border: 1px solid var(--border, #2e2f33);
      background: color-mix(in srgb, var(--bg-surface, #222326) 90%, transparent);
      padding: 12px;
      overflow: auto;
    }
    .prompt-editor-hint {
      margin-top: 8px;
      font-size: 11px;
      color: var(--muted, #8b8d93);
    }
    .login-info-fields {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 12px;
    }
    .login-info-fields label {
      display: block;
      font-size: 12px;
      font-weight: 500;
      color: var(--muted, #8b8d93);
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .learning-mode-note {
      margin-top: 6px;
      font-size: 11px;
      color: var(--muted, #8b8d93);
      line-height: 1.4;
    }
    @media (max-width: 860px) {
      .modal .row { grid-template-columns: minmax(0, 1fr); }
    }
    .btn-danger {
      background: color-mix(in srgb, var(--danger, #ef4444) 88%, #000);
      color: #fff;
      border-color: color-mix(in srgb, var(--danger, #ef4444) 70%, transparent);
    }
    .btn-danger:hover { filter: brightness(1.08); }
    .modal-loading-hint {
      margin: 0 0 12px;
      font-size: 12px;
      color: var(--muted, #8b8d93);
    }
    .confirm-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1250;
      display: grid;
      place-items: center;
      padding: 24px;
      background: rgba(0, 0, 0, 0.58);
      backdrop-filter: blur(8px);
    }
    .confirm-card {
      width: min(520px, calc(100vw - 40px));
      border: 1px solid color-mix(in srgb, var(--danger, #ef4444) 28%, var(--border));
      border-radius: 16px;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--danger, #ef4444) 14%, transparent), transparent 38%),
        var(--bg-elevated, #161920);
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.48);
      overflow: hidden;
    }
    .confirm-header {
      display: flex;
      gap: 12px;
      padding: 18px 20px 12px;
      align-items: flex-start;
    }
    .confirm-icon {
      width: 34px;
      height: 34px;
      border-radius: 12px;
      display: grid;
      place-items: center;
      background: color-mix(in srgb, var(--danger, #ef4444) 16%, transparent);
      color: var(--danger, #ef4444);
      font-weight: 800;
      flex: 0 0 auto;
    }
    .confirm-title {
      margin: 0;
      font-size: 16px;
      color: var(--text-strong, #f4f4f5);
      font-weight: 750;
    }
    .confirm-subtitle {
      margin-top: 5px;
      font-size: 12px;
      color: var(--muted, #8b8d93);
      line-height: 1.5;
    }
    .confirm-body {
      padding: 0 20px 16px 66px;
      display: grid;
      gap: 10px;
      color: var(--text, #d4d4d8);
      font-size: 13px;
      line-height: 1.55;
    }
    .confirm-warning {
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid color-mix(in srgb, var(--warn, #f59e0b) 28%, transparent);
      background: color-mix(in srgb, var(--warn, #f59e0b) 11%, transparent);
      color: var(--warn-muted, #fcd34d);
    }
    .confirm-error {
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid color-mix(in srgb, var(--danger, #ef4444) 30%, transparent);
      background: color-mix(in srgb, var(--danger, #ef4444) 12%, transparent);
      color: var(--danger-muted, #fca5a5);
    }
    .confirm-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      padding: 14px 20px 18px;
      border-top: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
      background: color-mix(in srgb, var(--bg, #0e1015) 42%, transparent);
    }
  `;

  @state() private _templates: ProjectTemplate[] = [];
  @state() private _selectedId: string | null = null;
  @state() private _loading = true;
  @state() private _templatesLoadError = "";
  @state() private _detailPromptLoadingId: string | null = null;
  private _templateDetailCache = new Map<string, ProjectTemplate>();
  @state() private _showEditModal = false;
  @state() private _editForm: Partial<ProjectTemplate> = {};
  @state() private _running = false;
  @state() private _showCreateModal = false;
  @state() private _creating = false;
  @state() private _showRunConfirmModal = false;
  @state() private _runModalStep: "learning" | "settings" = "learning";
  @state() private _runConfirmTemplateId: string | null = null;
  @state() private _runForm: RunConfirmForm = {};
  @state() private _runLearningModeLoading = false;
  @state() private _runNodeDeepLearningEnabled = true;
  @state() private _runLearningModeNote = "";
  @state() private _runPhaseTab: PromptPhaseKey = "aiPromptPart1";
  @state() private _runPromptMode: "write" | "preview" = "write";
  @state() private _editPhaseTab: PromptPhaseKey = "aiPromptPart1";
  @state() private _editPromptMode: "write" | "preview" = "write";
  @state() private _createPhaseTab: PromptPhaseKey = "aiPromptPart1";
  @state() private _createPromptMode: "write" | "preview" = "write";
  @state() private _createForm: Partial<ProjectTemplate> = {
    name: "",
    description: "",
    targetUrl: "",
    aiPrompt: DEFAULT_AI_PROMPT,
    exploreType: "pfm-full",
    timeBudgetMinutes: 30,
    ...getJobTypePromptParts("pfm-full"),
  };
  @state() private _savingEdit = false;
  @state() private _editDetailLoading = false;
  @state() private _runDetailLoading = false;
  @state() private _deleteConfirm: { id: string; name: string } | null = null;
  @state() private _deletingTemplate = false;
  @state() private _deleteError = "";
  @state() private _lastRunId: string | null = null;

  connectedCallback() {
    super.connectedCallback();
    this._loadTemplates();
    // Lightweight active-run probe only (no full executions.list on this tab).
    void executionsStore.fetchActiveExecutionProbe();
  }

  disconnectedCallback() {
    clearBodyPortal();
    super.disconnectedCallback();
  }

  protected override updated(changed: PropertyValues): void {
    super.updated(changed);
    this._syncBodyPortal();
  }

  private static _portalStylesCssText(): string {
    const styles = (EadProjectsView as typeof LitElement & { styles?: unknown }).styles;
    if (!styles) return "";
    if (Array.isArray(styles)) {
      return styles
        .map((chunk) => (chunk && typeof chunk === "object" && "cssText" in chunk ? String((chunk as { cssText: string }).cssText) : ""))
        .join("\n");
    }
    if (typeof styles === "object" && styles !== null && "cssText" in styles) {
      return String((styles as { cssText: string }).cssText);
    }
    return "";
  }

  private _syncBodyPortal(): void {
    const styleText = EadProjectsView._portalStylesCssText();
    const portals = html`
      ${styleText ? html`<style>${styleText}</style>` : nothing}
      ${this._showCreateModal ? this._renderCreateModal() : nothing}
      ${this._showRunConfirmModal ? this._renderRunConfirmModal() : nothing}
      ${this._showEditModal ? this._renderEditModal() : nothing}
      ${this._renderDeleteConfirmModal()}
    `;
    renderBodyPortal(portals);
  }

  private async _loadTemplates() {
    const cached = loadCachedTemplateSummaries();
    if (cached?.length) {
      this._templates = cached;
      this._loading = false;
      if (!this._selectedId) this._selectedId = cached[0].id;
    } else {
      this._loading = true;
    }
    this._templatesLoadError = "";

    const result = await hermesClient.request<{ templates: Array<Record<string, unknown>>; activeTemplateId: string | null }>(
      "templates.list",
    );
    if (result?.templates) {
      const rows = result.templates.map((row) => templateFromListSummary(row));
      this._templates = rows;
      saveCachedTemplateSummaries(rows);
      if (!this._selectedId && rows.length > 0) {
        this._selectedId = rows[0].id;
      }
    } else if (!cached?.length) {
      this._templatesLoadError =
        hermesClient.getLastRequestError() || "Could not load templates from the gateway.";
    }
    this._loading = false;
  }

  /** Fetch template metadata + prompts without the heavy pfm_nodes tree. */
  private async _ensureTemplateDetail(id: string | null): Promise<ProjectTemplate | null> {
    const templateId = String(id || "").trim();
    if (!templateId) return null;
    if (this._templateDetailCache.has(templateId)) return this._templateDetailCache.get(templateId) ?? null;

    const full = await hermesClient.request<ProjectTemplate>("templates.get", {
      id: templateId,
      lite: true,
    });
    if (!full?.id) return null;
    const summary = this._templates.find((t) => t.id === templateId);
    const merged: ProjectTemplate = {
      ...(summary ?? ({} as ProjectTemplate)),
      ...full,
      pfmNodes: [],
      pfmNodeCount: summary?.pfmNodeCount ?? full.pfmNodeCount,
    };
    this._templateDetailCache.set(templateId, merged);
    return merged;
  }

  /** Background load of AI prompt for the detail panel (lite GET — no pfm_nodes). */
  private async _loadDetailPrompt(id: string | null): Promise<void> {
    const templateId = String(id || "").trim();
    if (!templateId) return;
    const cached = this._templateDetailCache.get(templateId);
    if (cached?.aiPrompt?.trim()) return;

    this._detailPromptLoadingId = templateId;
    const full = await this._ensureTemplateDetail(templateId);
    this._detailPromptLoadingId = null;
    if (full?.id) this.requestUpdate();
  }

  private _selectTemplate(id: string): void {
    this._selectedId = id;
    void this._loadDetailPrompt(id);
  }

  private _getSelected(): ProjectTemplate | undefined {
    const id = this._selectedId;
    if (!id) return undefined;
    return this._templateDetailCache.get(id) ?? this._templates.find((t) => t.id === id);
  }

  private _pfmNodeCount(t: ProjectTemplate): number {
    return templatePfmNodeCount(t);
  }

  private async _saveTemplate() {
    const templateId = (this._editForm.id as string | undefined) || this._selectedId;
    if (!templateId) {
      notifyOperator({
        title: this._t("projects.saveFailedTitle"),
        message: this._t("projects.saveMissingId"),
        type: "error",
        modal: true,
      });
      return;
    }
    const snapshot = { ...this._editForm };
    this._savingEdit = true;
    this._showEditModal = false;
    try {
      const aiPrompt = this._isEadExploreProject(this._editForm)
        ? this._buildCombinedPromptFromParts(this._editForm)
        : (this._editForm.aiPrompt || "");
      const done = await hermesClient.request("templates.update", {
        templateId,
        name: this._editForm.name,
        description: this._editForm.description,
        exploreType: this._editForm.exploreType,
        targetUrl: this._editForm.targetUrl,
        aiPrompt,
        timeBudgetMinutes: this._editForm.timeBudgetMinutes,
      });
      if (done === undefined) {
        this._showEditModal = true;
        this._editForm = snapshot;
        notifyOperator({
          title: this._t("projects.saveFailedTitle"),
          message: this._t("projects.saveFailedMessage", { error: hermesClient.getLastRequestError() || "unknown error" }),
          type: "error",
          modal: true,
        });
        return;
      }
      this._selectedId = templateId;
      await this._loadTemplates();
    } catch (err) {
      this._showEditModal = true;
      this._editForm = snapshot;
      notifyOperator({
        title: this._t("projects.saveFailedTitle"),
        message: this._t("projects.saveFailedMessage", { error: err instanceof Error ? err.message : String(err) }),
        type: "error",
        modal: true,
      });
    } finally {
      this._savingEdit = false;
    }
  }

  private _applyTemplateToEditForm(t: ProjectTemplate): void {
    const parsedPhases = this._extractPromptParts(t.aiPrompt || "");
    this._editForm = {
      ...t,
      aiPromptPart1: t.aiPromptPart1 || parsedPhases.aiPromptPart1 || "",
      aiPromptPart2: t.aiPromptPart2 || parsedPhases.aiPromptPart2 || "",
    };
  }

  private _applyTemplateToRunForm(t: ProjectTemplate): void {
    const parsedPhases = this._extractPromptParts(t.aiPrompt || "");
    this._runForm = {
      ...t,
      aiPromptPart1: t.aiPromptPart1 || parsedPhases.aiPromptPart1 || "",
      aiPromptPart2: t.aiPromptPart2 || parsedPhases.aiPromptPart2 || "",
      runPurpose: "live_app_learning",
      evidenceSource: "live_app",
      learningExclusionReason: "",
      aiAnswerLanguage: this._defaultAnswerLanguage(),
      learningMode: "pfm_mindmap_learning",
    };
  }

  private _defaultAnswerLanguage(): AiAnswerLanguage {
    return defaultAnswerLanguage(this.locale);
  }

  private _resolveAnswerLanguageLabel(code: AiAnswerLanguage): string {
    return answerLanguageLabel(code, (key) => this._t(key));
  }

  private _appendAnswerLanguageInstruction(prompt: string, run: RunConfirmForm): string {
    const base = String(prompt || "").trim();
    if (!base) return base;
    const answerLanguage = run.aiAnswerLanguage || this._defaultAnswerLanguage();
    const instruction = [
      "## AI Answer Language",
      `- Always use ${this._resolveAnswerLanguageLabel(answerLanguage)} for operator-facing explanations, progress logs, and summaries unless the operator explicitly asks to switch language.`,
    ].join("\n");
    return `${base}\n\n${instruction}`.trim();
  }

  private _promptDeleteTemplate(): void {
    if (!this._selectedId) return;
    const selected = this._getSelected();
    const name = selected?.name || this._selectedId;
    this._deleteError = "";
    this._deleteConfirm = { id: this._selectedId, name };
  }

  private _closeDeleteConfirm(): void {
    if (this._deletingTemplate) return;
    this._deleteConfirm = null;
    this._deleteError = "";
  }

  private async _confirmDeleteTemplate(): Promise<void> {
    const pending = this._deleteConfirm;
    if (!pending?.id) return;
    this._deletingTemplate = true;
    this._deleteError = "";
    try {
      const done = await hermesClient.request("templates.delete", { id: pending.id });
      if (done === undefined) {
        this._deleteError =
          hermesClient.getLastRequestError() || this._t("projects.deleteConnectionError");
        return;
      }
      this._templateDetailCache.delete(pending.id);
      if (this._selectedId === pending.id) this._selectedId = null;
      this._deleteConfirm = null;
      await this._loadTemplates();
      notifyOperator({
        title: this._t("projects.deletedTitle"),
        message: this._t("projects.deletedMessage", { name: pending.name }),
        type: "success",
        modal: false,
        duration: 5000,
      });
    } finally {
      this._deletingTemplate = false;
    }
  }

  private _templateHasPrompt(t?: Partial<ProjectTemplate> | null): boolean {
    if (!t) return false;
    if (String(t.aiPrompt || "").trim()) return true;
    if (
      String(t.aiPromptPart1 || "").trim()
      || String(t.aiPromptPart2 || "").trim()
    ) {
      return true;
    }
    const parsed = this._extractPromptParts(String(t.aiPrompt || ""));
    return Boolean(
      String(parsed.aiPromptPart1 || "").trim()
      || String(parsed.aiPromptPart2 || "").trim(),
    );
  }

  private _hydrateTemplateDetailInBackground(
    templateId: string,
    opts?: { editOpen?: boolean; runOpen?: boolean },
  ): void {
    void (async () => {
      try {
        const full = await this._ensureTemplateDetail(templateId);
        if (full) {
          if (opts?.editOpen && this._showEditModal && String(this._editForm.id || "") === templateId) {
            this._applyTemplateToEditForm(full);
          }
          if (opts?.runOpen && this._showRunConfirmModal && this._runConfirmTemplateId === templateId) {
            this._applyTemplateToRunForm(full);
          }
        } else if (opts?.runOpen && this._showRunConfirmModal) {
          notifyOperator({
            title: this._t("projects.promptNotLoadedTitle"),
            message:
              hermesClient.getLastRequestError()
              || this._t("projects.promptNotLoadedMessage"),
            type: "warning",
            modal: false,
            duration: 8000,
          });
        }
      } finally {
        if (opts?.editOpen && this._showEditModal) {
          this._editDetailLoading = false;
        }
        if (opts?.runOpen && this._showRunConfirmModal && this._runConfirmTemplateId === templateId) {
          this._runDetailLoading = false;
        }
        this.requestUpdate();
      }
    })();
  }

  private async _openEditModal(t: ProjectTemplate) {
    const cached = this._templateDetailCache.get(t.id);
    this._applyTemplateToEditForm(cached ?? t);
    this._editPhaseTab = "aiPromptPart1";
    this._editPromptMode = "write";
    this._showEditModal = true;
    if (cached?.aiPrompt?.trim() || cached?.aiPromptPart1?.trim()) {
      this._editDetailLoading = false;
      return;
    }
    this._editDetailLoading = true;
    this._hydrateTemplateDetailInBackground(t.id, { editOpen: true });
  }

  private _closeEditModal() {
    if (this._running || this._savingEdit) return;
    this._showEditModal = false;
  }

  private _isEadExploreProject(project?: Partial<ProjectTemplate>): boolean {
    const exploreType = String(project?.exploreType || "pfm-full").toLowerCase();
    return !["website", "other"].includes(exploreType);
  }

  private _onRunPurposeChange(e: Event): void {
    const p = (e.target as HTMLSelectElement).value as NonNullable<ProjectExecute["runPurpose"]>;
    const evidence: NonNullable<ProjectExecute["evidenceSource"]> =
      p === "document_analysis" ? "document" : "live_app";
    this._runForm = { ...this._runForm, runPurpose: p, evidenceSource: evidence };
  }

  private _extractPromptParts(prompt: string): Pick<ProjectTemplate, "aiPromptPart1" | "aiPromptPart2"> {
    const raw = (prompt || "").trim();
    if (!raw) {
      return {
        aiPromptPart1: "",
        aiPromptPart2: "",
      };
    }
    const lines = raw.split(/\r?\n/);
    const buckets: Record<PromptPhaseKey, string[]> = {
      aiPromptPart1: [],
      aiPromptPart2: [],
    };
    let current: PromptPhaseKey | null = null;
    const phaseMatchers: Array<[PromptPhaseKey, RegExp]> = [
      ["aiPromptPart1", /phase\s*(1|i)\b|login\s*info|login\s*control|environment\s*&?\s*initialization|browser setup/i],
      ["aiPromptPart2", /phase\s*(2|ii)\b|explore\s*&?\s*discovery|pfm node identification|discovery|^\s*explore\s*$/i],
    ];
    for (const line of lines) {
      const normalized = line.trim();
      let detected: PromptPhaseKey | null = null;
      for (const [key, matcher] of phaseMatchers) {
        if (matcher.test(normalized)) {
          detected = key;
          break;
        }
      }
      if (detected) {
        current = detected;
        continue;
      }
      if (current) {
        buckets[current].push(line);
      }
    }
    if (!Object.values(buckets).some((items) => items.length > 0)) {
      return {
        aiPromptPart1: raw,
        aiPromptPart2: "",
      };
    }
    return {
      aiPromptPart1: buckets.aiPromptPart1.join("\n").trim(),
      aiPromptPart2: buckets.aiPromptPart2.join("\n").trim(),
    };
  }

  private _buildCombinedPromptFromParts(project: Partial<ProjectTemplate>): string {
    const sections = PHASE_PROMPT_META.map((phase) => {
      const body = String((project[phase.key] as string | undefined) || "").trim();
      if (!body) return "";
      return `${phase.title}\n${body}`;
    }).filter(Boolean);
    if (!sections.length) return "";
    return sections.join("\n\n");
  }

  private _renderLoginInfoEditor(
    part1: string,
    onChange: (next: string) => void,
  ) {
    const { username, password } = parseLoginInfoPrompt(part1);
    return html`
      <div class="login-info-fields">
        <div>
          <label>Username</label>
          <input
            type="text"
            autocomplete="off"
            placeholder="Login username"
            .value=${username}
            @input=${(e: Event) => {
              onChange(formatLoginInfoPrompt({
                username: (e.target as HTMLInputElement).value,
                password,
              }));
            }}
          />
        </div>
        <div>
          <label>Password</label>
          <input
            type="password"
            autocomplete="new-password"
            placeholder="Login password"
            .value=${password}
            @input=${(e: Event) => {
              onChange(formatLoginInfoPrompt({
                username,
                password: (e.target as HTMLInputElement).value,
              }));
            }}
          />
        </div>
      </div>
    `;
  }

  private _renderEadExplorePromptEditor(
    form: Partial<ProjectTemplate>,
    phaseTab: PromptPhaseKey,
    promptMode: "write" | "preview",
    onPhaseTab: (key: PromptPhaseKey) => void,
    onPromptMode: (mode: "write" | "preview") => void,
    onUpdatePart: (key: PromptPhaseKey, value: string) => void,
    options?: { showFooterHint?: boolean },
  ) {
    const activePhase = PHASE_PROMPT_META.find((phase) => phase.key === phaseTab) || PHASE_PROMPT_META[0];
    const content = String((form[activePhase.key] as string | undefined) || "");
    const previewHtml = activePhase.key === "aiPromptPart1"
      ? loginInfoPreviewMarkdown(content)
      : (content || "_No Explore prompt yet._");

    return html`
      <div class="prompt-editor-tabs">
        <div class="prompt-editor-phase-tabs">
          ${PHASE_PROMPT_META.map((phase) => html`
            <button
              class="prompt-editor-tab ${phaseTab === phase.key ? "active" : ""}"
              @click=${() => { onPhaseTab(phase.key); }}
            >${phase.label}</button>
          `)}
        </div>
        <div class="prompt-editor-mode-tabs">
          <button
            class="prompt-editor-tab ${promptMode === "write" ? "active" : ""}"
            @click=${() => { onPromptMode("write"); }}
          >Write</button>
          <button
            class="prompt-editor-tab ${promptMode === "preview" ? "active" : ""}"
            @click=${() => { onPromptMode("preview"); }}
          >Preview</button>
        </div>
      </div>
      <div class="prompt-editor-hint">${activePhase.title}</div>
      ${promptMode === "write"
        ? activePhase.key === "aiPromptPart1"
          ? this._renderLoginInfoEditor(content, (next) => { onUpdatePart("aiPromptPart1", next); })
          : html`
              <textarea
                .value=${content}
                @input=${(e: Event) => {
                  onUpdatePart(activePhase.key, (e.target as HTMLTextAreaElement).value);
                }}
              ></textarea>
            `
        : html`<div class="prompt-editor-preview">${unsafeHTML(toSanitizedMarkdownHtml(previewHtml))}</div>`}
      ${options?.showFooterHint
        ? html`<div class="prompt-editor-hint">EAD Explore uses Login Info (credentials) and Explore (running assignment).</div>`
        : nothing}
    `;
  }

  private _renderPromptEditorForEdit() {
    const isEad = this._isEadExploreProject(this._editForm);
    if (!isEad) {
      const content = this._editForm.aiPrompt || "";
      return html`
        <div class="prompt-editor-tabs">
          <div class="prompt-editor-mode-tabs">
            <button
              class="prompt-editor-tab ${this._editPromptMode === "write" ? "active" : ""}"
              @click=${() => { this._editPromptMode = "write"; }}
            >Write</button>
            <button
              class="prompt-editor-tab ${this._editPromptMode === "preview" ? "active" : ""}"
              @click=${() => { this._editPromptMode = "preview"; }}
            >Preview</button>
          </div>
        </div>
        ${this._editPromptMode === "write"
          ? html`
              <textarea
                .value=${content}
                @input=${(e: Event) => {
                  this._editForm = { ...this._editForm, aiPrompt: (e.target as HTMLTextAreaElement).value };
                }}
              ></textarea>
            `
          : html`<div class="prompt-editor-preview">${unsafeHTML(toSanitizedMarkdownHtml(content || "_No prompt content yet._"))}</div>`}
        <div class="prompt-editor-hint">Regular project prompt supports Markdown formatting.</div>
      `;
    }

    return this._renderEadExplorePromptEditor(
      this._editForm,
      this._editPhaseTab,
      this._editPromptMode,
      (key) => { this._editPhaseTab = key; },
      (mode) => { this._editPromptMode = mode; },
      (key, value) => { this._editForm = { ...this._editForm, [key]: value }; },
      { showFooterHint: true },
    );
  }

  private _renderPromptEditorForRun() {
    const isEad = this._isEadExploreProject(this._runForm);
    if (!isEad) {
      const content = this._runForm.aiPrompt || "";
      return html`
        <div class="prompt-editor-tabs">
          <div class="prompt-editor-mode-tabs">
            <button
              class="prompt-editor-tab ${this._runPromptMode === "write" ? "active" : ""}"
              @click=${() => { this._runPromptMode = "write"; }}
            >Write</button>
            <button
              class="prompt-editor-tab ${this._runPromptMode === "preview" ? "active" : ""}"
              @click=${() => { this._runPromptMode = "preview"; }}
            >Preview</button>
          </div>
        </div>
        ${this._runPromptMode === "write"
          ? html`
              <textarea
                .value=${content}
                @input=${(e: Event) => {
                  this._runForm = { ...this._runForm, aiPrompt: (e.target as HTMLTextAreaElement).value };
                }}
              ></textarea>
            `
          : html`<div class="prompt-editor-preview">${unsafeHTML(toSanitizedMarkdownHtml(content || "_No prompt content yet._"))}</div>`}
      `;
    }

    return this._renderEadExplorePromptEditor(
      this._runForm,
      this._runPhaseTab,
      this._runPromptMode,
      (key) => { this._runPhaseTab = key; },
      (mode) => { this._runPromptMode = mode; },
      (key, value) => { this._runForm = { ...this._runForm, [key]: value }; },
    );
  }

  private _renderPromptEditorForCreate() {
    const isEad = this._isEadExploreProject(this._createForm);
    if (!isEad) {
      const content = this._createForm.aiPrompt || "";
      return html`
        <div class="prompt-editor-tabs">
          <div class="prompt-editor-mode-tabs">
            <button
              class="prompt-editor-tab ${this._createPromptMode === "write" ? "active" : ""}"
              @click=${() => { this._createPromptMode = "write"; }}
            >Write</button>
            <button
              class="prompt-editor-tab ${this._createPromptMode === "preview" ? "active" : ""}"
              @click=${() => { this._createPromptMode = "preview"; }}
            >Preview</button>
          </div>
        </div>
        ${this._createPromptMode === "write"
          ? html`
              <textarea
                .value=${content}
                @input=${(e: Event) => {
                  this._createForm = { ...this._createForm, aiPrompt: (e.target as HTMLTextAreaElement).value };
                }}
              ></textarea>
            `
          : html`<div class="prompt-editor-preview">${unsafeHTML(toSanitizedMarkdownHtml(content || "_No prompt content yet._"))}</div>`}
      `;
    }

    return this._renderEadExplorePromptEditor(
      this._createForm,
      this._createPhaseTab,
      this._createPromptMode,
      (key) => { this._createPhaseTab = key; },
      (mode) => { this._createPromptMode = mode; },
      (key, value) => { this._createForm = { ...this._createForm, [key]: value }; },
      { showFooterHint: true },
    );
  }

  private _onCreateModalKeydown(e: KeyboardEvent) {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    this._closeCreateModal();
  }

  private _onEditModalKeydown(e: KeyboardEvent) {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    this._closeEditModal();
  }

  private _openCreateModal() {
    const exploreDefaults = getJobTypePromptParts("pfm-full");
    this._createForm = {
      name: "",
      description: "",
      targetUrl: "",
      exploreType: "pfm-full",
      timeBudgetMinutes: 30,
      aiPromptPart1: "",
      aiPromptPart2: exploreDefaults.aiPromptPart2,
    };
    this._createPhaseTab = "aiPromptPart1";
    this._createPromptMode = "write";
    this._showCreateModal = true;
  }

  private _closeCreateModal() {
    if (this._creating) return;
    this._showCreateModal = false;
  }

  private async _runNodeDeepLearningEligibility(templateId: string): Promise<boolean> {
    const fromStore = executionsStore.executions.filter(
      (ex) => String(ex.linkedTemplateId || "").trim() === templateId,
    );
    if (templateAllowsNodeDeepLearning(templateId, fromStore)) return true;
    const payload = await hermesClient.request<{ executions: ProjectExecute[] }>("executions.list", {
      templateId,
    });
    const rows = (Array.isArray(payload?.executions) ? payload.executions : []).map((row) =>
      normalizeExecutionFromApi(row),
    );
    return templateAllowsNodeDeepLearning(templateId, rows);
  }

  private _learningModeNodeDeepGuardMessage(): string {
    return "Node Deep Learning requires a previously finished valid run with committed DB PFM node tree.";
  }

  private async _openRunConfirmModal(templateId: string) {
    const summary = this._templates.find((x) => x.id === templateId);
    const cached = this._templateDetailCache.get(templateId);
    const seed = cached ?? summary;
    if (seed) {
      this._applyTemplateToRunForm(seed);
    } else {
      this._runForm = {};
    }
    this._runPhaseTab = "aiPromptPart1";
    this._runPromptMode = "write";
    this._runConfirmTemplateId = templateId;
    this._runModalStep = "learning";
    this._showRunConfirmModal = true;
    this._runNodeDeepLearningEnabled = true;
    this._runLearningModeLoading = true;
    this._runLearningModeNote = "Checking learning mode availability...";
    this._runDetailLoading = !this._templateHasPrompt(seed);
    if (this._runDetailLoading) {
      this._hydrateTemplateDetailInBackground(templateId, { runOpen: true });
    }
    try {
      const deepEnabled = await this._runNodeDeepLearningEligibility(templateId);
      this._runNodeDeepLearningEnabled = deepEnabled;
      if (!deepEnabled) {
        this._runForm = { ...this._runForm, learningMode: "pfm_mindmap_learning" };
        this._runLearningModeNote = this._learningModeNodeDeepGuardMessage();
      } else {
        this._runLearningModeNote = "";
      }
    } catch {
      // Keep modal usable; backend still enforces learning-mode guard.
      this._runNodeDeepLearningEnabled = true;
      this._runLearningModeNote = "";
    } finally {
      this._runLearningModeLoading = false;
    }
  }

  private _runFormHasPrompt(run: RunConfirmForm): boolean {
    if (String(run.aiPrompt || "").trim()) return true;
    return Boolean(this._buildCombinedPromptFromParts(run).trim());
  }

  private async _resolveRuntimePrompt(t: ProjectTemplate, run: RunConfirmForm): Promise<string> {
    const build = (source: Partial<ProjectTemplate>): string => {
      const merged = { ...t, ...source, ...run };
      return this._isEadExploreProject(merged)
        ? this._buildCombinedPromptFromParts(merged)
        : String(merged.aiPrompt || "");
    };
    let runtimePrompt = build(run);
    if (runtimePrompt.trim()) return this._appendAnswerLanguageInstruction(runtimePrompt, run);
    const cached = this._templateDetailCache.get(t.id);
    if (cached) {
      runtimePrompt = build(cached);
      if (runtimePrompt.trim()) return this._appendAnswerLanguageInstruction(runtimePrompt, run);
    }
    // Do not block Start Run on templates.get — use form/cache/defaults only.
    const exploreType = String(run.exploreType || t.exploreType || "pfm-full");
    runtimePrompt = this._buildCombinedPromptFromParts({
      ...getJobTypePromptParts(exploreType),
      ...run,
    });
    const resolved = runtimePrompt.trim() || DEFAULT_AI_PROMPT;
    return this._appendAnswerLanguageInstruction(resolved, run);
  }

  private _closeRunConfirmModal() {
    if (this._running) return;
    this._showRunConfirmModal = false;
    this._runModalStep = "learning";
    this._runConfirmTemplateId = null;
    this._runForm = {};
    this._runNodeDeepLearningEnabled = true;
    this._runLearningModeLoading = false;
    this._runLearningModeNote = "";
    this._runPhaseTab = "aiPromptPart1";
    this._runPromptMode = "write";
  }

  private _runConfirmTemplate(): ProjectTemplate | undefined {
    if (!this._runConfirmTemplateId) return undefined;
    return this._templates.find((t) => t.id === this._runConfirmTemplateId);
  }

  private _selectedLearningMode(): RunLearningMode {
    return this._runForm.learningMode === "node_deep_learning"
      ? "node_deep_learning"
      : "pfm_mindmap_learning";
  }

  private _setLearningMode(mode: RunLearningMode): void {
    if (mode === "node_deep_learning" && !this._runNodeDeepLearningEnabled) return;
    this._runForm = { ...this._runForm, learningMode: mode };
  }

  private _advanceRunModalToSettings(): void {
    this._runModalStep = "settings";
  }

  private _renderLearningModeStep(): ReturnType<typeof html> {
    const selected = this._selectedLearningMode();
    return html`
      <div class="learning-mode-cards" role="radiogroup" aria-label=${this._t("projects.chooseLearningModeTitle")}>
        <button
          type="button"
          role="radio"
          aria-checked=${selected === "pfm_mindmap_learning" ? "true" : "false"}
          class="learning-mode-card ${selected === "pfm_mindmap_learning" ? "selected" : ""}"
          @click=${() => this._setLearningMode("pfm_mindmap_learning")}
        >
          <span class="learning-mode-card__radio" aria-hidden="true"></span>
          <div class="learning-mode-card__body">
            <div class="learning-mode-card__title">${this._t("projects.learningModePfmTitle")}</div>
            <div class="learning-mode-card__desc">${this._t("projects.learningModePfmDesc")}</div>
          </div>
        </button>
        <button
          type="button"
          role="radio"
          aria-checked=${selected === "node_deep_learning" ? "true" : "false"}
          class="learning-mode-card ${selected === "node_deep_learning" ? "selected" : ""}"
          ?disabled=${!this._runNodeDeepLearningEnabled || this._runLearningModeLoading}
          @click=${() => this._setLearningMode("node_deep_learning")}
        >
          <span class="learning-mode-card__radio" aria-hidden="true"></span>
          <div class="learning-mode-card__body">
            <div class="learning-mode-card__title">${this._t("projects.learningModeDeepTitle")}</div>
            <div class="learning-mode-card__desc">${this._t("projects.learningModeDeepDesc")}</div>
          </div>
        </button>
      </div>
      ${this._runLearningModeNote
        ? html`<div class="learning-mode-note">${this._runLearningModeNote}</div>`
        : !this._runNodeDeepLearningEnabled
          ? html`<div class="learning-mode-note">${this._t("projects.learningModeRequiredFirst")}</div>`
          : ""}
    `;
  }

  private _runOrderValue(run: Partial<ProjectExecute>): number {
    const runNo = Number(run.runNumber || 0);
    if (Number.isFinite(runNo) && runNo > 0) return runNo;
    const created = Number((run as { createdAt?: number; created_at?: number }).createdAt ?? 0);
    if (Number.isFinite(created) && created > 0) return created;
    const started = Number(run.startTime || 0);
    if (Number.isFinite(started) && started > 0) return started;
    return 0;
  }

  private _latestActiveRun(): ProjectExecute | null {
    const rows = [...executionsStore.activeRuns];
    if (!rows.length) return null;
    rows.sort((a, b) => this._runOrderValue(b) - this._runOrderValue(a));
    return rows[0] || null;
  }

  private async _createTemplate() {
    const name = (this._createForm.name || "").trim();
    if (!name) {
      notifyOperator({
        title: this._t("projects.nameRequiredTitle"),
        message: this._t("projects.nameRequiredMessage"),
        type: "warning",
        modal: true,
      });
      return;
    }
    this._creating = true;
    try {
      const isEad = this._isEadExploreProject(this._createForm);
      const combinedPrompt = isEad
        ? this._buildCombinedPromptFromParts(this._createForm)
        : (this._createForm.aiPrompt || "");
      const created = await hermesClient.request<ProjectTemplate>("templates.create", {
        name,
        description: this._createForm.description || "",
        targetUrl: this._createForm.targetUrl || "",
        aiPrompt: combinedPrompt || DEFAULT_AI_PROMPT,
        aiPromptPart1: this._createForm.aiPromptPart1 || "",
        aiPromptPart2: this._createForm.aiPromptPart2 || "",
        exploreType: this._createForm.exploreType || "pfm-full",
        timeBudgetMinutes: this._createForm.timeBudgetMinutes || 30,
        createdBy: "operator",
        lastModifiedBy: "operator",
      });
      await this._loadTemplates();
      if (created?.id) {
        this._selectedId = created.id;
      }
      this._showCreateModal = false;
    } catch (err) {
      notifyOperator({
        title: this._t("projects.createFailedTitle"),
        message: this._t("projects.createFailedMessage", { error: err instanceof Error ? err.message : String(err) }),
        type: "error",
        modal: true,
      });
    } finally {
      this._creating = false;
    }
  }

  private async _runProject(template?: ProjectTemplate, runOverrides?: RunConfirmForm) {
    const t = template || this._getSelected();
    if (!t) return;
    const activeRun = (await executionsStore.fetchActiveExecutionProbe()) ?? null;
    if (activeRun?.id) {
      executionsStore.setSelectedExecutionId(activeRun.id, { skipDetailRefresh: true });
      this.dispatchEvent(new CustomEvent("navigate-tab", { detail: "runs", bubbles: true, composed: true }));
      notifyOperator({
        title: this._t("projects.runActiveTitle"),
        message: this._t("projects.runActiveMessage"),
        type: "warning",
        modal: true,
      });
      return;
    }
    const run: RunConfirmForm = { ...t, ...(runOverrides || {}) };
    const selectedLearningMode: RunLearningMode =
      run.learningMode === "node_deep_learning" ? "node_deep_learning" : "pfm_mindmap_learning";
    const enforcedLearningMode: RunLearningMode =
      !this._runNodeDeepLearningEnabled && selectedLearningMode === "node_deep_learning"
        ? "pfm_mindmap_learning"
        : selectedLearningMode;
    this._runDetailLoading = false;
    this._showRunConfirmModal = false;
    this._runModalStep = "learning";
    this._runConfirmTemplateId = null;
    this._runForm = {};
    this._running = true;
    this.requestUpdate();
    try {
      const runtimePrompt = await this._resolveRuntimePrompt(t, run);
      const runPayload = {
        templateId: t.id,
        name: `Run: ${run.name || t.name}`,
        description: run.description || "",
        targetUrl: run.targetUrl,
        aiPrompt: runtimePrompt,
        aiAnswerLanguage: run.aiAnswerLanguage || this._defaultAnswerLanguage(),
        exploreType: run.exploreType,
        timeBudgetMinutes: run.timeBudgetMinutes ?? t.timeBudgetMinutes ?? 30,
        costBudgetDollars: t.costBudgetDollars,
        runPurpose: run.runPurpose,
        learningMode: enforcedLearningMode,
        contributesToLearning: true,
        evidenceSource: run.evidenceSource,
        learningExclusionReason: run.learningExclusionReason?.trim() || undefined,
        inheritPfm: true,
      };
      const maxAttempts = 3;
      let result: Record<string, unknown> | undefined;
      let attemptsUsed = 0;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        attemptsUsed = attempt;
        result = await hermesClient.request<Record<string, unknown>>("executions.run", runPayload);
        if (result) break;
        const err = String(hermesClient.getLastRequestError() || "");
        const retryable =
          /econn|eperm|socket hang up|failed to fetch|network|timeout|timed out/i.test(err) ||
          /\b50[0-9]\b/.test(err);
        if (!retryable || attempt >= maxAttempts) break;
        await new Promise((resolve) => window.setTimeout(resolve, attempt * 900));
      }

      if (!result) {
        const err = hermesClient.getLastRequestError();
        notifyOperator({
          title: this._t("projects.runStartFailedTitle"),
          message: err
            ? `Failed to start run: ${err}\n\nTried ${attemptsUsed} time(s).\nIf this is a network/proxy issue, check:\n1) Hermes health: http://127.0.0.1:8642/health\n2) UI and gateway host/port alignment (VITE_HERMES_BASE_URL / dev proxy).`
            : "Failed to start run: no response from gateway.\nCheck network, gateway URL, and /v1/projects/executions/run.",
          type: "error",
          modal: true,
        });
      } else {
        const { executionId: newId, inheritedFromExecutionId } =
          unwrapPfmInheritanceMetadataFromRunResponse(result);
        if (newId) {
          this._lastRunId = newId;
          executionsStore.upsertExecutionFromRunResponse(result);
          executionsStore.setSelectedExecutionId(newId, { skipDetailRefresh: true });
          this.dispatchEvent(new CustomEvent("navigate-tab", { detail: "runs", bubbles: true, composed: true }));
          void executionsStore.refresh();
          if (inheritedFromExecutionId) {
            console.info(
              "[Projects] New run inherits PFM context from execution:",
              inheritedFromExecutionId,
            );
          }
        } else {
          console.warn("[Projects] executions.run returned no execution id; body:", result);
          this.dispatchEvent(new CustomEvent("navigate-tab", { detail: "runs", bubbles: true, composed: true }));
          void executionsStore.refresh();
          notifyOperator({
            title: this._t("projects.runIdMissingTitle"),
            message: this._t("projects.runIdMissingMessage"),
            type: "warning",
            modal: true,
          });
        }
      }
    } catch (err) {
      console.error("[Projects] Run failed:", err);
      notifyOperator({
        title: this._t("projects.runStartFailedTitle"),
        message: "Failed to start run: " + (err instanceof Error ? err.message : String(err)),
        type: "error",
        modal: true,
      });
    } finally {
      this._running = false;
      this.requestUpdate();
    }
  }

  render() {
    if (this._loading && !this._templates.length) {
      return html`<div class="loading">${this._t("projects.loading")}</div>`;
    }

    const selected = this._getSelected();

    return html`
      <div class="projects-container">
        <div class="template-list">
          <div class="template-list-header">
            <h2>${this._t("projects.templates", { count: this._templates.length })}</h2>
            <button class="btn btn-primary" @click=${() => this._openCreateModal()}>
              ${this._t("projects.new")}
            </button>
          </div>
          ${this._templatesLoadError
            ? html`<div style="margin:8px 12px;font-size:12px;color:var(--danger,#ef4444);">${this._templatesLoadError}</div>`
            : ""}
          ${this._loading && this._templates.length
            ? html`<div style="padding:8px 20px;font-size:12px;color:var(--muted);">${this._t("projects.refreshing")}</div>`
            : ""}
          ${this._templates.map(
            (t) => html`
              <div class="template-item ${t.id === this._selectedId ? "selected" : ""}" @click=${() => this._selectTemplate(t.id)}>
                <div class="name">${t.name}</div>
                <div class="url">${t.targetUrl || this._t("projects.noUrl")}</div>
                <div class="meta">${t.exploreType || "pfm-full"} · ${this._pfmNodeCount(t)} ${this._t("projects.nodes")} · ${this._t("projects.canonical")}:
                  ${t.canonicalPfmExecutionId?.trim()
                    ? html`<code style="font-size:10px;">${t.canonicalPfmExecutionId.trim().slice(0, 10)}…</code>`
                    : "—"}
                  · ${formatMs(t.lastModifiedAt)}</div>
              </div>
            `,
          )}
        </div>
        <div class="detail-panel">
          ${selected
            ? this._renderDetail(selected)
            : html`<div class="empty">${this._t("projects.selectTemplate")}</div>`}
        </div>
      </div>
    `;
  }

  private _renderCreateModal(): ReturnType<typeof html> {
    return html`
      <div class="modal-backdrop">
        <div class="modal" @keydown=${(e: KeyboardEvent) => this._onCreateModalKeydown(e)}>
          <div class="modal-header">
            <div class="modal-title-wrap">
              <h3>${this._t("projects.createTitle")}</h3>
              <div class="modal-subtitle">${this._t("projects.createSubtitle")}</div>
            </div>
            <div class="modal-header-actions">
              <button class="btn" ?disabled=${this._creating} @click=${() => this._closeCreateModal()}>
                ${this._t("projects.cancel")}
              </button>
              <button class="btn btn-primary" ?disabled=${this._creating} @click=${() => void this._createTemplate()}>
                ${this._creating ? this._t("projects.creating") : this._t("projects.create")}
              </button>
            </div>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="field-group">
                <label>${this._t("projects.field.name")}</label>
                <input
                  .value=${this._createForm.name || ""}
                  @input=${(e: Event) => {
                    this._createForm = { ...this._createForm, name: (e.target as HTMLInputElement).value };
                  }}
                />
              </div>
              <div class="field-group">
                <label>${this._t("projects.field.exploreType")}</label>
                <select
                  .value=${this._createForm.exploreType || "pfm-full"}
                  @change=${(e: Event) => {
                    const next = (e.target as HTMLSelectElement).value;
                    const exploreDefaults = getJobTypePromptParts(next);
                    this._createForm = {
                      ...this._createForm,
                      exploreType: next,
                      aiPromptPart2: exploreDefaults.aiPromptPart2,
                    };
                    this._createPhaseTab = "aiPromptPart2";
                  }}
                >
                  <option value="pfm-full">PFM Full</option>
                  <option value="pfm-spec">PFM Spec</option>
                  <option value="pfm-cs">PFM CS</option>
                  <option value="website">Website</option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="field-group">
                <label>${this._t("projects.field.targetUrl")}</label>
                <input
                  .value=${this._createForm.targetUrl || ""}
                  @input=${(e: Event) => {
                    this._createForm = { ...this._createForm, targetUrl: (e.target as HTMLInputElement).value };
                  }}
                />
              </div>
              <div class="field-group">
                <label>${this._t("projects.field.duration")}</label>
                <input
                  type="number"
                  .value=${String(this._createForm.timeBudgetMinutes || 30)}
                  @input=${(e: Event) => {
                    this._createForm = {
                      ...this._createForm,
                      timeBudgetMinutes: Number((e.target as HTMLInputElement).value) || 30,
                    };
                  }}
                />
              </div>
            </div>
            <div class="field-group">
              <label>${this._t("projects.field.description")}</label>
              <input
                .value=${this._createForm.description || ""}
                @input=${(e: Event) => {
                  this._createForm = { ...this._createForm, description: (e.target as HTMLInputElement).value };
                }}
              />
            </div>
            <div class="field-group" style="margin-bottom:0;">
              <label>${this._t("projects.field.aiPrompt")}</label>
              ${this._renderPromptEditorForCreate()}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderRunConfirmModal(): ReturnType<typeof html> {
    const learningStep = this._runModalStep === "learning";
    const modeLabel =
      this._selectedLearningMode() === "node_deep_learning"
        ? this._t("projects.learningModeDeepTitle")
        : this._t("projects.learningModePfmTitle");
    return html`
      <div
        class="modal-backdrop ${learningStep ? "modal-backdrop--learning" : ""}"
        @click=${() => this._closeRunConfirmModal()}
      >
        <div
          class="modal ${learningStep ? "modal--compact modal--learning" : ""}"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="modal-header">
            <div class="modal-title-wrap">
              <h3>
                ${learningStep
                  ? this._t("projects.chooseLearningModeTitle")
                  : this._t("projects.startRunTitle")}
              </h3>
              ${learningStep
                ? ""
                : html`
                    <div class="modal-subtitle">${this._t("projects.startRunSubtitle")}</div>
                  `}
            </div>
            <div class="modal-header-actions">
              ${learningStep
                ? html`
                    <button class="btn" ?disabled=${this._running} @click=${() => this._closeRunConfirmModal()}>
                      ${this._t("projects.cancel")}
                    </button>
                    <button
                      class="btn btn-primary"
                      ?disabled=${this._runLearningModeLoading}
                      @click=${() => this._advanceRunModalToSettings()}
                    >
                      ${this._t("projects.learningModeContinue")}
                    </button>
                  `
                : html`
                    <button class="btn" ?disabled=${this._running} @click=${() => { this._runModalStep = "learning"; }}>
                      ${this._t("projects.learningModeBack")}
                    </button>
                    <button class="btn" ?disabled=${this._running} @click=${() => this._closeRunConfirmModal()}>
                      ${this._t("projects.cancel")}
                    </button>
                    <button
                      class="btn btn-primary"
                      ?disabled=${this._running}
                      @click=${() => {
                        const runT = this._runConfirmTemplate();
                        if (runT) void this._runProject(runT, this._runForm);
                      }}
                    >
                      ${this._running ? this._t("projects.starting") : this._t("projects.startRun")}
                    </button>
                  `}
            </div>
          </div>
          ${(() => {
            const runT = this._runConfirmTemplate();
            if (!runT) {
              return html`<div class="modal-body"><div class="empty">${this._t("projects.templateNotFound")}</div></div>`;
            }
            if (learningStep) {
              return html`<div class="modal-body">${this._renderLearningModeStep()}</div>`;
            }
            return html`
              <div class="modal-body">
                <div class="learning-mode-summary">
                  <span>${modeLabel}</span>
                  <button type="button" @click=${() => { this._runModalStep = "learning"; }}>
                    ${this._t("projects.learningModeBack")}
                  </button>
                </div>
                ${this._runDetailLoading && !this._runFormHasPrompt(this._runForm)
                  ? html`<p class="modal-loading-hint">${this._t("projects.loadingPrompt")}</p>`
                  : ""}
                <div class="row">
                  <div class="field-group">
                    <label>${this._t("projects.field.runTitle")}</label>
                    <input
                      .value=${this._runForm.name || ""}
                      @input=${(e: Event) => {
                        this._runForm = { ...this._runForm, name: (e.target as HTMLInputElement).value };
                      }}
                    />
                  </div>
                  <div class="field-group">
                    <label>${this._t("projects.field.exploreType")}</label>
                    <select
                      .value=${this._runForm.exploreType || "pfm-full"}
                      @change=${(e: Event) => {
                        this._runForm = { ...this._runForm, exploreType: (e.target as HTMLSelectElement).value };
                      }}
                    >
                      <option value="pfm-full">PFM Full</option>
                      <option value="pfm-spec">PFM Spec</option>
                      <option value="pfm-cs">PFM CS</option>
                      <option value="website">Website</option>
                    </select>
                  </div>
                </div>
                <div class="row">
                  <div class="field-group">
                    <label>${this._t("projects.field.runPurpose")}</label>
                    <select
                      .value=${this._runForm.runPurpose || "live_app_learning"}
                      @change=${this._onRunPurposeChange}
                    >
                      <option value="live_app_learning">Live app — learning</option>
                      <option value="live_app_testing">Live app — testing (skip learning index)</option>
                      <option value="document_analysis">Document analysis — learning</option>
                    </select>
                  </div>
                  <div class="field-group">
                    <label>${this._t("projects.field.evidenceFocus")}</label>
                    <select
                      .value=${this._runForm.evidenceSource || "live_app"}
                      @change=${(e: Event) => {
                        this._runForm = {
                          ...this._runForm,
                          evidenceSource: (e.target as HTMLSelectElement).value as NonNullable<
                            ProjectExecute["evidenceSource"]
                          >,
                        };
                      }}
                    >
                      <option value="live_app">Live app</option>
                      <option value="document">Documents</option>
                      <option value="hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>
                <div class="row">
                  <div class="field-group">
                    <label>${this._t("projects.field.runningDuration")}</label>
                    <input
                      type="number"
                      .value=${String(this._runForm.timeBudgetMinutes || 30)}
                      @input=${(e: Event) => {
                        this._runForm = {
                          ...this._runForm,
                          timeBudgetMinutes: Number((e.target as HTMLInputElement).value) || 30,
                        };
                      }}
                    />
                  </div>
                  <div class="field-group">
                    <label>${this._t("projects.field.targetUrl")}</label>
                    <input
                      .value=${this._runForm.targetUrl || ""}
                      @input=${(e: Event) => {
                        this._runForm = { ...this._runForm, targetUrl: (e.target as HTMLInputElement).value };
                      }}
                    />
                  </div>
                </div>
                <div class="field-group">
                  <label>${this._t("projects.field.aiAnswerLanguage")}</label>
                  <ead-ai-answer-language-field
                    .value=${this._runForm.aiAnswerLanguage || this._defaultAnswerLanguage()}
                    .locale=${this.locale}
                    @language-change=${(event: CustomEvent<{ value: AiAnswerLanguage }>) => {
                      this._runForm = {
                        ...this._runForm,
                        aiAnswerLanguage: event.detail.value,
                      };
                    }}
                  ></ead-ai-answer-language-field>
                </div>
                <div class="field-group">
                  <label>${this._t("projects.field.description")}</label>
                  <textarea
                    style="min-height:80px;"
                    .value=${this._runForm.description || ""}
                    @input=${(e: Event) => {
                      this._runForm = { ...this._runForm, description: (e.target as HTMLTextAreaElement).value };
                    }}
                  ></textarea>
                </div>
                <div class="field-group" style="margin-bottom:0;">
                  <label>${this._t("projects.field.aiPrompt")}</label>
                  ${this._renderPromptEditorForRun()}
                </div>
              </div>
            `;
          })()}
        </div>
      </div>
    `;
  }

  private _renderEditModal(): ReturnType<typeof html> {
    return html`
      <div class="modal-backdrop">
        <div class="modal" @keydown=${(e: KeyboardEvent) => this._onEditModalKeydown(e)}>
          <div class="modal-header">
            <div class="modal-title-wrap">
              <h3>${this._t("projects.editTitle")}</h3>
              <div class="modal-subtitle">${this._t("projects.editSubtitle")}</div>
            </div>
            <div class="modal-header-actions">
              <button class="btn" ?disabled=${this._savingEdit} @click=${() => this._closeEditModal()}>${this._t("projects.cancel")}</button>
              <button class="btn btn-primary" ?disabled=${this._savingEdit} @click=${() => this._saveTemplate()}>
                ${this._savingEdit ? this._t("projects.saving") : this._t("projects.save")}
              </button>
            </div>
          </div>
          <div class="modal-body">
            ${this._editDetailLoading && !String(this._editForm.aiPromptPart1 || this._editForm.aiPrompt || "").trim()
              ? html`<p class="modal-loading-hint">${this._t("projects.loadingPrompt")}</p>`
              : ""}
            <div class="row">
              <div class="field-group">
                <label>${this._t("projects.field.name")}</label>
                <input .value=${this._editForm.name || ""} @input=${(e: Event) => { this._editForm = { ...this._editForm, name: (e.target as HTMLInputElement).value }; }} />
              </div>
              <div class="field-group">
                <label>${this._t("projects.field.exploreType")}</label>
                <select
                  .value=${this._editForm.exploreType || "pfm-full"}
                  @change=${(e: Event) => { this._editForm = { ...this._editForm, exploreType: (e.target as HTMLSelectElement).value }; }}
                >
                  <option value="pfm-full">PFM Full</option>
                  <option value="pfm-spec">PFM Spec</option>
                  <option value="pfm-cs">PFM CS</option>
                  <option value="website">Website</option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="field-group">
                <label>${this._t("projects.field.targetUrl")}</label>
                <input .value=${this._editForm.targetUrl || ""} @input=${(e: Event) => { this._editForm = { ...this._editForm, targetUrl: (e.target as HTMLInputElement).value }; }} />
              </div>
              <div class="field-group">
                <label>${this._t("projects.field.timeBudget")}</label>
                <input type="number" .value=${String(this._editForm.timeBudgetMinutes || "")} @input=${(e: Event) => { this._editForm = { ...this._editForm, timeBudgetMinutes: Number((e.target as HTMLInputElement).value) || undefined }; }} />
              </div>
            </div>
            <div class="field-group">
              <label>${this._t("projects.field.description")}</label>
              <input .value=${this._editForm.description || ""} @input=${(e: Event) => { this._editForm = { ...this._editForm, description: (e.target as HTMLInputElement).value }; }} />
            </div>
            <div class="field-group" style="margin-bottom:0;">
              <label>${this._t("projects.field.aiPrompt")}</label>
              ${this._renderPromptEditorForEdit()}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private _renderDeleteConfirmModal(): ReturnType<typeof html> {
    const pending = this._deleteConfirm;
    if (!pending) return html``;
    return html`
      <div class="confirm-backdrop" @click=${() => this._closeDeleteConfirm()}>
        <div
          class="confirm-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-template-title"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="confirm-header">
            <div class="confirm-icon">!</div>
            <div>
              <h3 id="delete-template-title" class="confirm-title">${this._t("projects.deleteConfirm")}</h3>
              <div class="confirm-subtitle">${pending.name}</div>
            </div>
          </div>
          <div class="confirm-body">
            <div>${this._t("projects.deleteBody")}</div>
            <div class="confirm-warning">${this._t("projects.deleteWarning")}</div>
            ${this._deleteError
              ? html`<div class="confirm-error">${this._deleteError}</div>`
              : ""}
          </div>
          <div class="confirm-actions">
            <button class="btn" type="button" ?disabled=${this._deletingTemplate} @click=${() => this._closeDeleteConfirm()}>
              ${this._t("projects.notNow")}
            </button>
            <button
              class="btn btn-danger"
              type="button"
              ?disabled=${this._deletingTemplate}
              @click=${() => void this._confirmDeleteTemplate()}
            >
              ${this._deletingTemplate ? "Deleting…" : this._t("projects.deleteTemplate")}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderDetail(t: ProjectTemplate) {
    const cached = this._templateDetailCache.get(t.id);
    const prompt = (cached?.aiPrompt || t.aiPrompt || "").trim();
    const promptLoading = this._detailPromptLoadingId === t.id;
    return html`
      <h3>${t.name}</h3>
      <div class="actions">
        <button class="btn btn-run" ?disabled=${this._running} @click=${() => void this._openRunConfirmModal(t.id)}>${this._running ? this._t("projects.starting") : `▶ ${this._t("projects.startRunBtn")}`}</button>
        <button class="btn btn-primary" @click=${() => void this._openEditModal(t)}>${this._t("projects.editBtn")}</button>
        <button class="btn" ?disabled=${this._deletingTemplate} @click=${() => this._promptDeleteTemplate()}>${this._t("projects.deleteBtn")}</button>
      </div>
      <div class="field-group" style="margin-top: 20px;">
        <label>${this._t("projects.field.targetUrl")}</label>
        <div class="value">${t.targetUrl || "—"}</div>
      </div>
      <div class="field-group">
        <label>${this._t("projects.field.exploreType")}</label>
        <div class="value"><span class="badge">${t.exploreType || "pfm-full"}</span></div>
      </div>
      <div class="field-group">
        <label>${this._t("projects.field.description")}</label>
        <div class="value">${t.description || "—"}</div>
      </div>
      <div class="field-group">
        <label>${this._t("projects.field.aiPrompt")}</label>
        <div class="value sidebar-markdown" style="background: rgba(30,31,34,0.5); padding: 16px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.08);">
          ${
            promptLoading
              ? html`<span style="color:var(--muted);font-size:12px;">${this._t("projects.loadingPromptShort")}</span>`
              : prompt
                ? html`<div .innerHTML=${toSanitizedMarkdownHtml(prompt)}></div>`
                : "—"
          }
        </div>
      </div>
      <div class="field-group">
        <label>${this._t("projects.field.authMode")}</label>
        <div class="value">${cached?.authMode || t.authMode || "none"}</div>
      </div>
      <div class="field-group">
        <label>${this._t("projects.field.timeBudgetSimple")}</label>
        <div class="value">${t.timeBudgetMinutes ? `${t.timeBudgetMinutes} min` : "—"}</div>
      </div>
      <div class="field-group">
        <label>${this._t("projects.field.pfmNodes")}</label>
        <div class="value">${this._pfmNodeCount(t)} ${this._t("projects.nodes")}</div>
      </div>
      <div class="field-group">
        <label>${this._t("projects.field.created")}</label>
        <div class="value">${formatMs(t.createdAt)} by ${t.createdBy || "—"}</div>
      </div>
    `;
  }

  // Edit now uses modal; inline edit form removed.
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-projects-view": EadProjectsView;
  }
}
